import React, { forwardRef } from 'react'
import { InTextLink } from '../../shared/interfaces/project/document/in-text-link/in-text-link.interface'
import { convertQuads } from '../../utils/convert-quads'
import { useCoords } from '../../hooks/use-coords'
import { Quads } from '../../shared/interfaces/quads.interface'
import { useSelector } from 'react-redux'
import { selectShowClauseReferences } from '../../redux/viewer-slice'
import { useGetUserProfileQuery } from '../../redux/api-slice'

interface PageOverlayDefinitionProps {
  link: InTextLink
  page: number
  shouldElevate: boolean
}

const PageOverlayDefinition = forwardRef<
  HTMLButtonElement,
  PageOverlayDefinitionProps
>(({ link, page, shouldElevate }, ref) => {
  const { getCoordsFromPageSection } = useCoords()
  const showClauseRefs = useSelector(selectShowClauseReferences)
  const { data: user } = useGetUserProfileQuery(undefined)

  /* Hide clause refs if toggle is off or feature flag is disabled */
  if (
    link.source?.source_type === 2 &&
    (!showClauseRefs || !user?.feature_flags?.section_reference)
  )
    return null

  const quads = (link.quads as Quads[])?.[0].x1
    ? (link.quads[0] as Quads)
    : convertQuads(link.quads, page)

  const { width, height, leftOffset, topOffset } = getCoordsFromPageSection(
    quads,
    page,
    'document-viewer',
    quads.y3 > quads.y2
  )

  const handleClick = (e: React.MouseEvent) => {
    if (link.source?.source_type !== 2) return
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    /* call global handler with label + page */
    window.referenceClickHandler?.(
      rect.left,
      rect.top,
      link.source.text ?? '', // section label
      link.page // page number
    )
  }

  return (
    <button
      ref={ref}
      onClick={handleClick}
      className={`absolute ${
        link.source?.source_type === 2
          ? 'bg-blue-800 opacity-30 hover:ring-1 hover:ring-blue-400/70'
          : 'bg-blue-800 mix-blend-lighten'
      } ${shouldElevate ? 'z-50' : 'z-30'} cursor-pointer`}
      style={{
        width: `${width}%`,
        height: `${height}%`,
        left: `${leftOffset}px`,
        top: `${topOffset}%`,
      }}
    />
  )
})

PageOverlayDefinition.displayName = 'PageOverlayDefinition'
export default PageOverlayDefinition
